import path from 'node:path';

import { ValidationResult } from '../../../packages/torrentzip/index.js';
import type MappableSemaphore from '../../async/mappableSemaphore.js';
import type ProgressBar from '../../console/progressBar.js';
import { ProgressBarSymbol } from '../../console/progressBar.js';
import TokenReplacementException from '../../exceptions/tokenReplacementException.js';
import type FileFactory from '../../factories/fileFactory.js';
import type DAT from '../../models/dats/dat.js';
import Disk from '../../models/dats/disk.js';
import type Game from '../../models/dats/game.js';
import MergedDiscGame from '../../models/dats/mergedDiscGame.js';
import type ROM from '../../models/dats/rom.js';
import type Archive from '../../models/files/archives/archive.js';
import ArchiveEntry from '../../models/files/archives/archiveEntry.js';
import ArchiveFile from '../../models/files/archives/archiveFile.js';
import Chd from '../../models/files/archives/chd/chd.js';
import ChdBinCue from '../../models/files/archives/chd/chdBinCue.js';
import Zip from '../../models/files/archives/zip.js';
import File from '../../models/files/file.js';
import ZeroSizeFile from '../../models/files/zeroSizeFile.js';
import type IndexedFiles from '../../models/indexedFiles.js';
import type Options from '../../models/options.js';
import { ZipFormat } from '../../models/options.js';
import ROMWithFiles from '../../models/romWithFiles.js';
import WriteCandidate from '../../models/writeCandidate.js';
import type { OutputPath } from '../../modules/candidates/utils/outputFactory.js';
import OutputFactory from '../../modules/candidates/utils/outputFactory.js';
import ArrayUtil from '../../utils/arrayUtil.js';
import FsUtil from '../../utils/fsUtil.js';
import IntlUtil from '../../utils/intlUtil.js';
import Module from '../module.js';

/**
 * For every {@link Game} in a {@link DAT}, look for its {@link ROM}s in the scanned ROM list,
 * and return a set of candidate files.
 */
export default class CandidateGenerator extends Module {
  private readonly options: Options;
  private readonly fileFactory: FileFactory;
  private readonly readerSemaphore: MappableSemaphore;

  constructor(
    options: Options,
    progressBar: ProgressBar,
    fileFactory: FileFactory,
    readerSemaphore: MappableSemaphore,
  ) {
    super(progressBar, CandidateGenerator.name);
    this.options = options;
    this.fileFactory = fileFactory;
    this.readerSemaphore = readerSemaphore;
  }

  /**
   * Generate the candidates.
   */
  async generate(dat: DAT, indexedFiles: IndexedFiles): Promise<WriteCandidate[]> {
    if (indexedFiles.getFiles().length === 0) {
      this.prefixedLogger.trace(`${dat.getName()}: no input ROMs to make candidates from`);
      return [];
    }

    this.prefixedLogger.trace(`${dat.getName()}: generating candidates`);
    this.progressBar.setSymbol(ProgressBarSymbol.CANDIDATE_GENERATING);
    this.progressBar.resetProgress(dat.getGames().length);

    // For each game, try to generate a candidate
    const candidates = (
      await this.readerSemaphore.map(dat.getGames(), async (game) => {
        this.progressBar.incrementInProgress();
        const childBar = this.progressBar.addChildBar({
          name: game.getName(),
        });

        let gameCandidates: WriteCandidate[] = [];
        try {
          gameCandidates = await this.buildCandidatesForGame(dat, game, indexedFiles);
          if (gameCandidates.length > 0) {
            this.prefixedLogger.trace(
              `${dat.getName()}: ${game.getName()}: found candidate: ${gameCandidates[0]
                .getRomsWithFiles()
                .map((rwf) => rwf.getInputFile().toString())
                .reduce(ArrayUtil.reduceUnique(), [])
                .join(', ')}`,
            );
          }
        } catch (error) {
          // Ignore token replacement errors, just don't add the candidate
          if (!(error instanceof TokenReplacementException)) {
            throw error;
          }
          this.prefixedLogger.debug(
            `${dat.getName()}: ${game.getName()}: failed to generate candidate: ${error.message}`,
          );
        } finally {
          childBar.delete();
        }

        this.progressBar.incrementCompleted();
        return gameCandidates;
      })
    ).flat();

    const size = candidates
      .flatMap((candidate) => candidate.getRomsWithFiles())
      .reduce((sum, romWithFiles) => sum + romWithFiles.getRom().getSize(), 0);
    this.prefixedLogger.trace(
      `${dat.getName()}: generated ${FsUtil.sizeReadable(size)} of ${IntlUtil.toLocaleString(candidates.length)} candidate${candidates.length === 1 ? '' : 's'}`,
    );

    this.prefixedLogger.trace(`${dat.getName()}: done generating candidates`);
    return candidates;
  }

  private async buildCandidatesForGame(
    dat: DAT,
    game: Game,
    indexedFiles: IndexedFiles,
  ): Promise<WriteCandidate[]> {
    const gameRoms = [
      ...game.getRoms(),
      ...(this.options.getExcludeDisks() ? [] : game.getDisks()),
    ];

    const romsAndInputFiles = gameRoms.map((rom): [ROM, File[]] => {
      if (
        !this.options.shouldLink() &&
        rom.getSize() === 0 &&
        (rom.getCrc32() === '00000000' ||
          rom.getMd5() === 'd41d8cd98f00b204e9800998ecf8427e' ||
          rom.getSha1() === 'da39a3ee5e6b4b0d3255bfef95601890afd80709' ||
          rom.getSha256() === 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855')
      ) {
        // It's an empty file. Prefer real, matching input files (e.g. an empty entry inside an
        // archive) when they exist, so the containing archive can be recognized as holding every
        // ROM and raw-copied/moved intact. Fall back to synthesizing one when none are found.
        const foundFiles = indexedFiles.findFiles(rom);
        if (foundFiles.length > 0) {
          return [rom, foundFiles];
        }
        return [rom, [ZeroSizeFile.getInstance()]];
      }

      return [rom, indexedFiles.findFiles(rom)];
    });
    if (
      romsAndInputFiles.length > 0 &&
      romsAndInputFiles.reduce((sum, [, inputFiles]) => sum + inputFiles.length, 0) === 0
    ) {
      // No matching input files were found
      return [];
    }

    const romsAndLegalInputFiles = this.filterLegalInputFilesForGame(dat, game, romsAndInputFiles);
    const romsToOptimalInputFile = this.findOptimalInputFileForGame(
      dat,
      game,
      gameRoms,
      romsAndLegalInputFiles,
      indexedFiles,
    );

    // For each Game's ROM, find the matching File
    const romsAndRomsWithFiles = (await Promise.all(
      gameRoms.map(
        async (rom) => await this.buildRomRomWithFilesPair(dat, game, rom, romsToOptimalInputFile),
      ),
    )) satisfies [ROM, ROMWithFiles | undefined][];
    const foundRomsWithFiles = romsAndRomsWithFiles
      .map(([, romWithFiles]) => romWithFiles)
      .filter((romWithFiles) => romWithFiles !== undefined);
    if (romsAndRomsWithFiles.length > 0 && foundRomsWithFiles.length === 0) {
      // The Game has ROMs, but none were found
      this.prefixedLogger.trace(
        `${dat.getName()}: ${game.getName()}: could not find any valid input file for any ROM, game cannot be written`,
      );
      return [];
    }

    const foundRomsWithArchiveFiles = await this.buildRomsWithArchiveEntries(
      dat,
      game,
      foundRomsWithFiles,
    );
    if (foundRomsWithArchiveFiles.length < foundRomsWithFiles.length) {
      // Some input files were filtered out; it is expected that a message has already been logged
      return [];
    }

    // Ignore the Game if not every File is present
    const missingRoms = romsAndRomsWithFiles
      .filter(([, romWithFiles]) => !romWithFiles)
      .map(([rom]) => rom);
    if (missingRoms.length > 0 && !this.options.getAllowIncompleteSets()) {
      if (foundRomsWithArchiveFiles.length > 0) {
        this.logMissingRomFiles(dat, game, foundRomsWithArchiveFiles, missingRoms);
      }
      return [];
    }

    // Ignore the Game with conflicting input->output files
    if (this.hasConflictingOutputFiles(dat, foundRomsWithArchiveFiles)) {
      return [];
    }

    // If the found files have excess, and we aren't allowing it, then return no candidate
    if (
      !this.options.shouldZip() &&
      !this.options.shouldExtract() &&
      !this.options.getAllowExcessSets() &&
      this.hasExcessFiles(dat, game, foundRomsWithArchiveFiles, indexedFiles)
    ) {
      return [];
    }

    return await this.generateWriteCandidates(dat, game, foundRomsWithArchiveFiles);
  }

  private filterLegalInputFilesForGame(
    dat: DAT,
    game: Game,
    romsToInputFiles: [ROM, File[]][],
  ): [ROM, File[]][] {
    if (romsToInputFiles.length === 0) {
      // There aren't any ROMs, so there's nothing to filter
      return romsToInputFiles;
    }

    return romsToInputFiles.map(([rom, inputFiles]): [ROM, File[]] => {
      if (inputFiles.length === 0) {
        // There aren't any matched files, so there's nothing to filter
        return [rom, inputFiles];
      }

      const isAreRawWriting =
        this.options.shouldWrite() &&
        !this.options.shouldExtractRom(rom) &&
        !this.options.shouldZipRom(rom);

      const filteredInputFiles = inputFiles.filter((inputFile) => {
        if (
          !isAreRawWriting &&
          inputFile instanceof ArchiveEntry &&
          !(rom instanceof Disk) &&
          !inputFile.canExtract()
        ) {
          // We need to read the extracted file, but can't, so we can't use this file
          this.prefixedLogger.trace(
            `${dat.getName()}: ${game.getName()}: ${rom.getName()}: can't use archive because it can't be extracted: ${inputFile.toString()}`,
          );
          return false;
        }

        if (
          isAreRawWriting &&
          inputFile instanceof ArchiveEntry &&
          rom.getName().trim() !== '' &&
          inputFile.getArchive().hasMeaningfulEntryPaths()
        ) {
          const outputPath = OutputFactory.getPath(this.options, dat, game, rom, inputFile);
          if (outputPath.entryPath !== inputFile.getExtractedFilePath()) {
            // The input file is an ArchiveEntry that we won't rewrite and its name doesn't match
            // what we want it to be
            this.prefixedLogger.trace(
              `${dat.getName()}: ${game.getName()}: ${rom.getName()}: can't use archive because the entry '${inputFile.getExtractedFilePath()}' doesn't have the correct path '${outputPath.entryPath}'`,
            );
            return false;
          }
        }

        return true;
      });
      return [rom, filteredInputFiles];
    });
  }

  private findOptimalInputFileForGame(
    dat: DAT,
    game: Game,
    gameRoms: ROM[],
    romsAndInputFiles: [ROM, File[]][],
    indexedFiles: IndexedFiles,
  ): Map<ROM, File> {
    if (romsAndInputFiles.length === 0) {
      // There aren't any ROMs, so there's nothing to find
      return new Map();
    }

    if (game instanceof MergedDiscGame) {
      // Resolve each disc independently so every disc is the single-archive case the code already
      // handles correctly (including pulling an unknown .cue from the same CHD that holds its .bins).
      return this.findOptimalInputFilesForMergedDiscGame(
        dat,
        game,
        romsAndInputFiles,
        indexedFiles,
      );
    }

    const archiveWithEveryRom = this.findArchiveFileWithEveryRomForGame(
      dat,
      game,
      gameRoms,
      romsAndInputFiles,
      indexedFiles,
    );
    if (archiveWithEveryRom !== undefined) {
      return archiveWithEveryRom;
    }

    return new Map(
      romsAndInputFiles
        .filter(([, inputFiles]) => inputFiles.length > 0)
        .map(([rom, inputFiles]) => {
          // Trust that ROMIndexer applied any preferences we wanted
          return [rom, inputFiles[0]];
        }),
    );
  }

  /**
   * Resolve input files for a {@link MergedDiscGame} one sub-game at a time. Any ROM not resolved
   * to a single containing archive falls back to its first matched input file.
   */
  private findOptimalInputFilesForMergedDiscGame(
    dat: DAT,
    game: MergedDiscGame,
    romsAndInputFiles: [ROM, File[]][],
    indexedFiles: IndexedFiles,
  ): Map<ROM, File> {
    // All intermediate matching is keyed by a stable ROM name + hash code string rather than by ROM
    // instance, so it survives any earlier reinstantiation
    const entryKey = (rom: ROM): string => `${rom.getName()}|${rom.hashCode()}`;
    const entriesByKey = new Map<string, [ROM, File[]]>(
      romsAndInputFiles.map((romAndInputFiles) => [
        entryKey(romAndInputFiles[0]),
        romAndInputFiles,
      ]),
    );

    // Resolve each sub-game's single containing archive, keyed by entryKey
    const filesByKey = new Map<string, File>();
    for (const subGame of game.getSubGames()) {
      const subGameRoms = subGame.getRoms();
      const subGameRomsAndInputFiles = subGameRoms
        .map((subGameRom) => entriesByKey.get(entryKey(subGameRom)))
        .filter((entry) => entry !== undefined);
      if (subGameRomsAndInputFiles.length === 0) {
        continue;
      }

      const archiveWithEveryRom = this.findArchiveFileWithEveryRomForGame(
        dat,
        subGame,
        subGameRoms,
        subGameRomsAndInputFiles,
        indexedFiles,
      );
      if (archiveWithEveryRom === undefined) {
        continue;
      }
      for (const [rom, inputFile] of archiveWithEveryRom) {
        filesByKey.set(entryKey(rom), inputFile);
      }
    }

    const resolved = new Map<ROM, File>();
    for (const [rom, inputFiles] of romsAndInputFiles) {
      const inputFile = filesByKey.get(entryKey(rom)) ?? inputFiles.at(0);
      if (inputFile !== undefined) {
        resolved.set(rom, inputFile);
      }
    }
    return resolved;
  }

  /**
   * Find a single input {@link Archive} that contains every one of a {@link Game}'s {@link ROM}s, and
   * return a map from each ROM to its matching entry within that archive. Preferring one archive for
   * the whole game avoids output-path conflicts when raw-copying and avoids leaving archives partially
   * used when zipping. Returns `undefined` when extracting (the source archive doesn't matter) or when
   * no single archive holds every ROM, leaving the caller to fall back to per-ROM matching. ROMs with
   * no matching entry are omitted from the returned map, so it is always a `Map<ROM, File>` of only
   * resolved ROMs.
   */
  private findArchiveFileWithEveryRomForGame(
    dat: DAT,
    game: Game,
    gameRoms: ROM[],
    romsAndInputFiles: [ROM, File[]][],
    indexedFiles: IndexedFiles,
  ): Map<ROM, File> | undefined {
    if (gameRoms.length === 0) {
      return undefined;
    }

    if (gameRoms.every((rom) => this.options.shouldExtractRom(rom))) {
      // We're extracting files, we don't particularly care where they come from, respect any
      // previous sorting
      return undefined;
    }

    // Detect if there is one input archive that contains every ROM, and prefer to use its entries.
    // If we don't do this, there are two situations that can happen:
    //  1. When raw writing (i.e. `igir copy`, `igir move`) archives of games with multiple ROMs, if
    //      some of those ROMs exist in multiple input archives, then you may get a conflict warning
    //      that multiple input files want to write to the same output file - and nothing will be
    //      written.
    //  2. When moving + archiving (i.e. `igir move zip`) games with multiple ROMs, if there are
    //      duplicates of the ROMs in some input archives, then you may get a warning that some of
    //      the input archives won't be deleted because not every entry in it was used for an
    //      output file.

    // Group this Game's ROMs by the input Archives that contain them
    const inputArchivesToRoms = romsAndInputFiles.reduce((map, [rom, files]) => {
      for (const file of files) {
        if (!(file instanceof ArchiveEntry)) {
          continue;
        }
        const archive: Archive = file.getArchive();
        // We need to filter out duplicate ROMs because of Games that contain duplicate ROMs, e.g.
        //  optical media games that have the same track multiple times.
        if (!map.has(archive)) {
          map.set(archive, new Set());
        }
        map.get(archive)?.add(rom);
      }
      return map;
    }, new Map<Archive, Set<ROM>>());

    // Filter to the Archives that contain every ROM in this Game
    const archivesWithEveryRom = [...inputArchivesToRoms]
      .filter(([inputArchive, roms]) => {
        if (
          Array.from(roms, (rom) => rom.hashCode()).join(',') ===
          gameRoms.map((rom) => rom.hashCode()).join(',')
        ) {
          return true;
        }
        // If there is a CHD with every .bin file, and we're raw-copying it, then assume its .cue
        // file is accurate
        return (
          inputArchive instanceof ChdBinCue &&
          gameRoms.every(
            (rom) => !(this.options.shouldZipRom(rom) || this.options.shouldExtractRom(rom)),
          ) &&
          CandidateGenerator.onlyCueFilesMissingFromChd(game, [...roms])
        );
      })
      .map(([archive]) => archive);

    const filesByPath = indexedFiles.getFilesByFilePath();
    const filteredArchivesWithEveryRom = archivesWithEveryRom
      .filter((archive) => {
        // Filter out Archives with excess entries
        const unusedEntries = this.findArchiveUnusedEntryPaths(
          archive,
          romsAndInputFiles.flatMap(([, inputFiles]) => inputFiles),
          indexedFiles,
        );
        if (unusedEntries.length > 0) {
          this.prefixedLogger.trace(
            `${dat.getName()}: ${game.getName()}: not preferring archive that contains every ROM, plus the excess entries:\n${unusedEntries.map((unusedEntry) => `  ${unusedEntry.toString()}`).join('\n')}`,
          );
          return false;
        }

        // If we're zipping, only consider zip archives that we might be able to raw-write
        return !this.options.shouldZip() || archive instanceof Zip;
      })
      .toSorted((a, b) => {
        // First, prefer the archive that isn't from the output directory
        const aIsOutputFile = filesByPath
          .get(a.getFilePath())
          ?.some((file) => file.getCanBeCandidateInput())
          ? 0
          : 1;
        const bIsOutputFile = filesByPath
          .get(b.getFilePath())
          ?.some((file) => file.getCanBeCandidateInput())
          ? 0
          : 1;
        if (aIsOutputFile !== bIsOutputFile) {
          return aIsOutputFile - bIsOutputFile;
        }

        // Then, prefer the archive with the least number of entries
        const aEntries = filesByPath.get(a.getFilePath())?.length ?? 0;
        const bEntries = filesByPath.get(b.getFilePath())?.length ?? 0;
        if (aEntries !== bEntries) {
          return aEntries - bEntries;
        }

        // Then, prefer non-CHDs
        const aChd = a instanceof Chd ? 1 : 0;
        const bChd = b instanceof Chd ? 1 : 0;
        if (aChd !== bChd) {
          return bChd - aChd;
        }

        // Then, prefer archives whose filename contains the game name; this is particularly
        // helpful when working without DATs
        const aGameName = path.basename(a.getFilePath()).includes(game.getName()) ? 1 : 0;
        const bGameName = path.basename(b.getFilePath()).includes(game.getName()) ? 1 : 0;
        return bGameName - aGameName;
      });

    const archiveWithEveryRom = filteredArchivesWithEveryRom.at(0);
    if (archiveWithEveryRom === undefined) {
      return undefined;
    }
    if (filteredArchivesWithEveryRom.length > 1) {
      this.prefixedLogger.trace(
        `${dat.getName()}: ${game.getName()}: preferring input archive that contains every ROM: '${archiveWithEveryRom.getFilePath()}'; ignoring:\n${filteredArchivesWithEveryRom
          .slice(1)
          .map((archive) => `  ${archive.getFilePath()}`)
          .join('\n')}`,
      );
    } else {
      this.prefixedLogger.trace(
        `${dat.getName()}: ${game.getName()}: preferring input archive that contains every ROM: '${archiveWithEveryRom.getFilePath()}'`,
      );
    }

    // An Archive was found, use that as the only possible input file
    // For each of this Game's ROMs, find the matching ArchiveEntry from this Archive
    return new Map(
      romsAndInputFiles.flatMap(([rom, inputFiles]) => {
        const archiveEntries = inputFiles.filter(
          (inputFile) =>
            inputFile.getFilePath() === archiveWithEveryRom.getFilePath() &&
            inputFile instanceof ArchiveEntry &&
            inputFile.getArchive() === archiveWithEveryRom,
        );
        let archiveEntry =
          // If there are multiple entries in the archive that could be used for this ROM, try to
          // pick the best one based on its name. Picking the right entry may let us raw-write this
          // archive if it's a zip.
          archiveEntries.find(
            (archiveEntry) =>
              archiveEntry instanceof ArchiveEntry && archiveEntry.getEntryPath() === rom.getName(),
          ) ??
          // Otherwise, just grab the first one.
          archiveEntries.at(0);

        if (
          !archiveEntry &&
          rom.getName().toLowerCase().endsWith('.cue') &&
          archiveWithEveryRom instanceof ChdBinCue
        ) {
          // We assumed this CHD was fine above, find its .cue file
          archiveEntry = filesByPath
            .get(archiveWithEveryRom.getFilePath())
            ?.find((file) => file.getExtractedFilePath().toLowerCase().endsWith('.cue'));
        }

        return archiveEntry === undefined ? [] : [[rom, archiveEntry]];
      }),
    );
  }

  private async buildRomRomWithFilesPair(
    dat: DAT,
    game: Game,
    rom: ROM,
    romsToInputFiles: Map<ROM, File>,
  ): Promise<[ROM, ROMWithFiles | undefined]> {
    let inputFile = romsToInputFiles.get(rom);
    if (inputFile === undefined) {
      return [rom, undefined];
    }

    // If we're not writing (report only) then just use the input file for the output file
    if (!this.options.shouldWrite() && !this.options.shouldTest()) {
      return [rom, new ROMWithFiles(rom, inputFile, inputFile)];
    }

    /**
     * WARN(cemmer): {@link inputFile} may not be an exact match for {@link rom}. There are two
     * situations we can be in:
     *  - {@link rom} is headered and so is {@link inputFile}, so we have an exact match
     *  - {@link rom} is headerless but {@link inputFile} is headered, because we know how to
     *    remove headers from ROMs - but we can't remove headers in all writing modes!
     */

    // If the input file is headered...
    if (
      inputFile.getFileHeader() &&
      // ...and we can't rewrite the file
      !this.options.shouldWrite()
    ) {
      // ...then forget the input file's header, so that it doesn't report as incorrect when tested
      inputFile = inputFile.withoutFileHeader();
    }

    // If the file is trimmed...
    if (
      inputFile.getPaddings().length > 0 &&
      // ...and we can't rewrite the file
      !this.options.shouldWrite()
    ) {
      // ...then forget the input file's padding, so that it doesn't report as incorrect when tested
      inputFile = inputFile.withPaddings([]);
    }

    // If the input file is headered...
    if (
      inputFile.getFileHeader() &&
      // ...and we want a headered ROM
      ((inputFile.getCrc32() !== undefined && inputFile.getCrc32() === rom.getCrc32()) ||
        (inputFile.getMd5() !== undefined && inputFile.getMd5() === rom.getMd5()) ||
        (inputFile.getSha1() !== undefined && inputFile.getSha1() === rom.getSha1()) ||
        (inputFile.getSha256() !== undefined && inputFile.getSha256() === rom.getSha256())) &&
      // ...and we shouldn't remove the header
      !this.options.canRemoveHeader(path.extname(inputFile.getExtractedFilePath()))
    ) {
      // ...then forget the input file's header, so that we don't later remove it
      this.prefixedLogger.trace(
        `${dat.getName()}: ${game.getName()}: not removing header, ignoring that one was found for: ${inputFile.toString()}`,
      );
      inputFile = inputFile.withoutFileHeader();
    }

    // If the input file is trimmed...
    if (
      inputFile.getPaddings().length > 0 &&
      // ...and we want a trimmed ROM
      ((inputFile.getCrc32() !== undefined && inputFile.getCrc32() === rom.getCrc32()) ||
        (inputFile.getMd5() !== undefined && inputFile.getMd5() === rom.getMd5()) ||
        (inputFile.getSha1() !== undefined && inputFile.getSha1() === rom.getSha1()) ||
        (inputFile.getSha256() !== undefined && inputFile.getSha256() === rom.getSha256()))
    ) {
      // ...then forget the input file's padding, so that we don't later add it back
      this.prefixedLogger.trace(
        `${dat.getName()}: ${game.getName()}: not adding padding, ignoring that file is trimmed: ${inputFile.toString()}`,
      );
      inputFile = inputFile.withPaddings([]);
    }

    // If the input file is headered...
    if (
      inputFile.getFileHeader() &&
      // ...and we DON'T want a headered ROM
      !(
        (inputFile.getCrc32() !== undefined && inputFile.getCrc32() === rom.getCrc32()) ||
        (inputFile.getMd5() !== undefined && inputFile.getMd5() === rom.getMd5()) ||
        (inputFile.getSha1() !== undefined && inputFile.getSha1() === rom.getSha1()) ||
        (inputFile.getSha256() !== undefined && inputFile.getSha256() === rom.getSha256())
      ) &&
      // ...and we're writing file links
      this.options.shouldLink()
    ) {
      // ...then we can't use this file
      this.prefixedLogger.trace(
        `${dat.getName()}: ${game.getName()}: can't use headered ROM as target for link: ${inputFile.toString()}`,
      );
      return [rom, undefined];
    }

    // If the input file is trimmed...
    if (inputFile.getPaddings().length > 0) {
      const desiredPadding = inputFile.getPaddings().find((padding) => {
        return (
          (padding.getCrc32() !== undefined && padding.getCrc32() === rom.getCrc32()) ||
          (padding.getMd5() !== undefined && padding.getMd5() === rom.getMd5()) ||
          (padding.getSha1() !== undefined && padding.getSha1() === rom.getSha1()) ||
          (padding.getSha256() !== undefined && padding.getSha256() === rom.getSha256())
        );
      });
      // ...but we don't want any of the known paddings
      if (desiredPadding === undefined) {
        // ...then forget all paddings so that none get added during writing
        inputFile = inputFile.withPaddings([]);
      } else {
        // ...else only remember the padding if we want to add it back
        inputFile = inputFile.withPaddings(
          this.options.getTrimAddPadding() ? [desiredPadding] : [],
        );
      }
    }

    try {
      const outputFile = await this.getOutputFile(dat, game, rom, inputFile);
      if (outputFile === undefined) {
        return [rom, undefined];
      }
      const romWithFiles = new ROMWithFiles(rom, inputFile, outputFile);
      return [rom, romWithFiles];
    } catch (error) {
      this.prefixedLogger.error(`${dat.getName()}: ${game.getName()}: ${error}`);
      return [rom, undefined];
    }
  }

  private async shouldGenerateArchiveFile(
    dat: DAT,
    game: Game,
    romsWithFiles: ROMWithFiles[],
  ): Promise<string | undefined> {
    // TODO(cemmer): this is an issue when raw-writing at the same time, it causes extraction
    if (this.options.shouldDir2Dat()) {
      // We want to keep the scanned archive entries for dir2dat
      return 'generating a dir2dat';
    }

    if (this.options.getZipDatName()) {
      // All candidates will be later combined, so we can't raw-copy this archive
      return 'zipping all ROMs from a DAT together later';
    }

    // Checks for all archive types, zips and otherwise
    for (const romWithFiles of romsWithFiles) {
      const rom = romWithFiles.getRom();
      const inputFile = romWithFiles.getInputFile();

      if (!(inputFile instanceof ArchiveEntry)) {
        // Non-archived files can't be made into an ArchiveFile
        return `input file isn't from an archive`;
      }

      if (
        // If the input file is headered...
        inputFile.getFileHeader() &&
        // ...and we want an unheadered ROM
        ((inputFile.getCrc32WithoutHeader() !== undefined &&
          inputFile.getCrc32WithoutHeader() !== rom.getCrc32()) ||
          (inputFile.getMd5WithoutHeader() !== undefined &&
            inputFile.getMd5WithoutHeader() !== rom.getMd5()) ||
          (inputFile.getSha1WithoutHeader() !== undefined &&
            inputFile.getSha1WithoutHeader() !== rom.getSha1()) ||
          (inputFile.getSha256WithoutHeader() !== undefined &&
            inputFile.getSha256WithoutHeader() !== rom.getSha256()))
      ) {
        // ...then we can't use this archive as-is
        return 'need to strip an existing ROM header during writing';
      }

      if (
        // If the input file is trimmed...
        inputFile.getPaddings().length > 0 &&
        // ...and we want a padded ROM
        inputFile.getPaddings().some((padding) => {
          return (
            (padding.getCrc32() !== undefined && padding.getCrc32() === rom.getCrc32()) ||
            (padding.getMd5() !== undefined && padding.getMd5() === rom.getMd5()) ||
            (padding.getSha1() !== undefined && padding.getSha1() === rom.getSha1()) ||
            (padding.getSha256() !== undefined && padding.getSha256() === rom.getSha256())
          );
        })
      ) {
        // ...then we can't use this archive as-is
        return 'input file is trimmed and a padded file is expected';
      }

      if (this.options.shouldExtractRom(rom)) {
        // We want to extract the ROM, we shouldn't make an ArchiveFile
        return 'input archive is being extracted';
      }

      if (this.options.shouldZipRom(rom) && !(inputFile.getArchive() instanceof Zip)) {
        // We want to zip the ROM, and the input file isn't already in a zip
        return "input archive is being zipped and it isn't already a zip";
      }

      if (rom.getName().trim() !== '' && inputFile.getArchive().hasMeaningfulEntryPaths()) {
        const outputPath = OutputFactory.getPath(this.options, dat, game, rom, inputFile);
        if (outputPath.entryPath !== inputFile.getExtractedFilePath()) {
          // This file doesn't have the correct entry path, we need to rewrite it
          return `input entry path '${inputFile.getExtractedFilePath()}' doesn't have the correct path '${outputPath.entryPath}'`;
        }
      }
    }

    if (
      romsWithFiles.length > 1 &&
      romsWithFiles
        .map((romWithFiles) => romWithFiles.getOutputFile().getFilePath())
        .reduce(ArrayUtil.reduceUnique(), []).length > 1 &&
      game instanceof MergedDiscGame
    ) {
      // This Game is the result of 2+ discs merged together, and we're writing at least 2 files.
      // Skip all the single input archive checks below.
      return undefined;
    }

    if (
      romsWithFiles.filter(
        ArrayUtil.filterUniqueMapped((romWithFiles) => romWithFiles.getInputFile().getFilePath()),
      ).length !== 1
    ) {
      // Every input file has to be coming from the same archive
      return 'input files are coming from different archives';
    }
    const inputArchive = (romsWithFiles[0].getInputFile() as ArchiveEntry<Archive>).getArchive();

    // If we're allowed to re-zip archives, check if the zip file is a valid structured archive or not
    if (
      inputArchive instanceof Zip &&
      romsWithFiles.every((romWithFiles) => this.options.shouldZipRom(romWithFiles.getRom()))
    ) {
      const tzValidationResult = await this.fileFactory.tzValidationFrom(inputArchive);
      if (tzValidationResult === ValidationResult.INVALID) {
        return "input zip isn't a valid TorrentZip or RVZSTD archive";
      }

      if (
        this.options.getZipFormat() === ZipFormat.TORRENTZIP &&
        tzValidationResult !== ValidationResult.VALID_TORRENTZIP
      ) {
        // The input file isn't a TorrentZip, it needs to be rewritten
        return "input zip isn't a valid TorrentZip archive (it's RVZSTD)";
      }

      if (
        this.options.getZipFormat() === ZipFormat.RVZSTD &&
        tzValidationResult !== ValidationResult.VALID_RVZSTD
      ) {
        // The input file isn't RVZSTD, it needs to be rewritten
        return "input zip isn't a valid RVZSTD archive (it's TorrentZip)";
      }
    }

    return undefined;
  }

  private async buildRomsWithArchiveEntries(
    dat: DAT,
    game: Game,
    foundRomsWithFiles: ROMWithFiles[],
  ): Promise<ROMWithFiles[]> {
    if (foundRomsWithFiles.length === 0) {
      // There aren't any ROMs
      return foundRomsWithFiles;
    }

    // If every matched input file is from the same archive, and we can raw-copy that entire
    // archive, then treat the file as "raw" so it can be copied/moved as-is
    const generateArchiveFilePreventionReason = await this.shouldGenerateArchiveFile(
      dat,
      game,
      foundRomsWithFiles,
    );
    return (
      await Promise.all(
        foundRomsWithFiles.map(async (romWithFiles) => {
          if (
            generateArchiveFilePreventionReason !== undefined &&
            !(romWithFiles.getRom() instanceof Disk)
          ) {
            if (
              romWithFiles.getInputFile() instanceof ArchiveEntry &&
              this.options.shouldWrite() &&
              !this.options.shouldExtractRom(romWithFiles.getRom()) &&
              !this.options.shouldZipRom(romWithFiles.getRom())
            ) {
              // We must be able to use the entire archive as-is if we're not extracting or zipping
              this.prefixedLogger.trace(
                `${dat.getName()}: ${game.getName()}: ${romWithFiles.getRom().getName()}: can't raw-write archive: ${generateArchiveFilePreventionReason}`,
              );
              return undefined;
            }

            // Otherwise, we can use the input file however it is, File or ArchiveEntry
            return romWithFiles;
          }

          const oldInputFile = romWithFiles.getInputFile();
          if (!(oldInputFile instanceof ArchiveEntry)) {
            // This shouldn't happen, but if it does, just ignore
            return romWithFiles;
          }

          /**
           * Note: we're delaying checksum calculations for now,
           * {@link CandidateArchiveFileHasher} will handle it later
           */
          try {
            const newInputFile = new ArchiveFile(oldInputFile as ArchiveEntry<Archive>, {
              size: await FsUtil.size(oldInputFile.getFilePath()),
              checksumBitmask: oldInputFile.getChecksumBitmask(),
            });
            return romWithFiles.withInputFile(newInputFile);
          } catch (error) {
            this.prefixedLogger.warn(`${dat.getName()}: ${game.getName()}: ${error}`);
            return undefined;
          }
        }),
      )
    ).filter((romWithFiles) => romWithFiles !== undefined);
    // Note: we'll have duplicate input->output files here, but we can't get rid of them until
    // checking for missing ROMs or excess files
  }

  private logMissingRomFiles(
    dat: DAT,
    game: Game,
    foundRomsWithFiles: ROMWithFiles[],
    missingRoms: ROM[],
  ): void {
    if (
      foundRomsWithFiles.every(
        (romWithFiles) => romWithFiles.getInputFile() instanceof ZeroSizeFile,
      )
    ) {
      // We didn't find any actual files on disk, so prevent some log spam
      return;
    }

    let message = `${dat.getName()}: ${game.getName()}: found ${IntlUtil.toLocaleString(foundRomsWithFiles.length)} file${foundRomsWithFiles.length === 1 ? '' : 's'}, missing ${IntlUtil.toLocaleString(missingRoms.length)} file${missingRoms.length === 1 ? '' : 's'}:`;
    for (const rom of missingRoms) {
      message += `\n  ${rom.getName()}`;
    }
    this.prefixedLogger.trace(message);

    // Warn if the only reason we're missing files is that we don't know if we can rewrite .cue
    // files extracted from CHDs with the right contents such that it matches a DAT perfectly
    if (missingRoms.every((rom) => rom.getName().toLowerCase().endsWith('.cue'))) {
      const chdInputFile = foundRomsWithFiles
        .map((romWithFiles) => romWithFiles.getInputFile())
        .find(
          (inputFile): inputFile is ArchiveEntry<ChdBinCue> =>
            inputFile instanceof ArchiveEntry && inputFile.getArchive() instanceof ChdBinCue,
        );
      const missingCueRom = missingRoms.find(
        (rom) => this.options.shouldExtractRom(rom) || this.options.shouldZipRom(rom),
      );
      if (chdInputFile !== undefined && missingCueRom !== undefined) {
        this.prefixedLogger.trace(
          `${dat.getName()}: ${game.getName()}: cannot extract .cue sheets from CHDs because Igir doesn't know how to rewrite the track filenames accurately: ${chdInputFile.getArchive().getFilePath()}`,
        );
      }
    }
  }

  private hasConflictingOutputFiles(dat: DAT, romsWithFiles: ROMWithFiles[]): boolean {
    // If we're not writing, then don't bother looking for conflicts
    if (!this.options.shouldWrite()) {
      return false;
    }

    // If there are less than two files to write, then there can't be any conflicts
    if (romsWithFiles.length < 2) {
      return false;
    }

    // For all the ROMs for a Game+Release, find all non-archive output files that have a duplicate
    //  output file path. In other words, there are multiple input files that want to write to the
    //  same output file.
    const duplicateOutputPaths = romsWithFiles
      .map((romWithFiles) => romWithFiles.getOutputFile())
      .filter((outputFile) => !(outputFile instanceof ArchiveEntry))
      .map((outputFile) => outputFile.getFilePath())
      // Is a duplicate output path
      .filter((outputPath, idx, outputPaths) => outputPaths.indexOf(outputPath) !== idx)
      // Only return one copy of duplicate output paths
      .reduce(ArrayUtil.reduceUnique(), [])
      .toSorted((a, b) => a.localeCompare(b));
    if (duplicateOutputPaths.length === 0) {
      // There are no duplicate non-archive output file paths
      return false;
    }

    let hasConflict = false;
    for (const duplicateOutput of duplicateOutputPaths) {
      // For an output path that has multiple input paths, filter to only the unique input paths,
      //  and if there are still multiple input file paths then we won't be able to resolve this
      //  at write time
      const conflictedInputFiles = romsWithFiles
        .filter((romWithFiles) => romWithFiles.getOutputFile().getFilePath() === duplicateOutput)
        .map((romWithFiles) => romWithFiles.getInputFile().toString())
        .reduce(ArrayUtil.reduceUnique(), []);
      if (conflictedInputFiles.length > 1) {
        hasConflict = true;
        let message = `${dat.getName()}: no single archive contains all necessary files, cannot ${this.options.writeString()} these different input files to: ${duplicateOutput}:`;
        for (const conflictedInputFile of conflictedInputFiles) {
          message += `\n  ${conflictedInputFile}`;
        }
        this.prefixedLogger.warn(message);
      }
    }
    return hasConflict;
  }

  /**
   * Given a {@link Game}, return true if all conditions are met:
   * - The {@link Game} only has .bin and .cue files
   * - Out of the {@link ROM}s that were found in an input directory for the {@link Game}, every
   *    .bin was found but at least one .cue file is missing
   * This is only relevant when we are raw-copying CHD files, where it is difficult to ensure that
   * the .cue file is accurate.
   */
  private static onlyCueFilesMissingFromChd(game: Game, foundRoms: ROM[]): boolean {
    if (game.getRoms().length < 2) {
      // The game has to have at least two ROMs to have a .cue and at least one .bin
      return false;
    }

    if (foundRoms.length === 0) {
      // No ROMs were found, including any .bin files
      return false;
    }

    // Only games with only bin/cue files can have only a cue file missing
    let hasCue = false;
    let hasBin = false;
    let hasOther = false;
    for (const rom of game.getRoms()) {
      const romName = rom.getName().toLowerCase();
      if (romName.endsWith('.cue')) {
        hasCue = true;
      } else if (romName.endsWith('.bin')) {
        hasBin = true;
      } else {
        hasOther = true;
      }
    }
    if (!hasCue || !hasBin || hasOther) {
      // This is not a .cue/.bin only game
      return false;
    }

    const foundRomNames = new Set(foundRoms.map((rom) => rom.getName()));
    return game
      .getRoms()
      .every(
        (rom) => foundRomNames.has(rom.getName()) || rom.getName().toLowerCase().endsWith('.cue'),
      );
  }

  private hasExcessFiles(
    dat: DAT,
    game: Game,
    romsWithFiles: ROMWithFiles[],
    indexedFiles: IndexedFiles,
  ): boolean {
    if (romsWithFiles.length === 0) {
      // No matching files were found, so there can't be any excess
      return false;
    }

    // For this Game, find every input file that is an ArchiveEntry
    const inputArchiveEntries = romsWithFiles
      // We need to rehydrate information from IndexedFiles because raw-copying/moving archives
      // would have lost this information
      .map((romWithFiles) => {
        const rom = romWithFiles.getRom();
        const inputFile = romWithFiles.getInputFile();
        const candidates = indexedFiles
          .findFiles(rom)
          .filter(
            (foundFile) =>
              foundFile.getFilePath() === inputFile.getFilePath() &&
              (inputFile instanceof ArchiveEntry || inputFile instanceof ArchiveFile) &&
              foundFile instanceof ArchiveEntry &&
              inputFile.getArchive() === foundFile.getArchive(),
          );
        // Prefer the entry whose path matches the ROM name. This matters whenever a Game has
        // multiple ROMs that share a checksum but have different names - since those ROMs would
        // otherwise all resolve to the same first matching entry, hiding which entries are
        // actually used.
        return (
          candidates.find(
            (foundFile) =>
              foundFile instanceof ArchiveEntry &&
              foundFile.getExtractedFilePath() === rom.getName(),
          ) ?? candidates.at(0)
        );
      })
      .filter((inputFile) => inputFile instanceof ArchiveEntry || inputFile instanceof ArchiveFile);
    // ...then translate those ArchiveEntries into a list of unique Archives
    const inputArchives = inputArchiveEntries
      .map((archiveEntry) => archiveEntry.getArchive())
      .filter(ArrayUtil.filterUniqueMapped((archive) => archive.getFilePath()));

    if (
      inputArchives.length === 1 &&
      inputArchives[0] instanceof ChdBinCue &&
      CandidateGenerator.onlyCueFilesMissingFromChd(
        game,
        romsWithFiles.map((romWithFiles) => romWithFiles.getRom()),
      )
    ) {
      // We couldn't match the CHD's .cue files, so don't consider them as excess
      return false;
    }

    for (const inputArchive of inputArchives) {
      const unusedEntries = this.findArchiveUnusedEntryPaths(
        inputArchive,
        inputArchiveEntries,
        indexedFiles,
      );
      if (unusedEntries.length > 0) {
        this.prefixedLogger.trace(
          `${dat.getName()}: ${game.getName()}: cannot use '${inputArchive.getFilePath()}' as an input file, it has the excess entries:\n${unusedEntries.map((unusedEntry) => `  ${unusedEntry.toString()}`).join('\n')}`,
        );
        return true;
      }
    }

    return false;
  }

  /**
   * Given an input {@link archive} and a set of {@link inputFiles} that match to a {@link ROM} from
   * a {@link Game}, determine if every entry from the {@link archive} was matched.
   */
  private findArchiveUnusedEntryPaths(
    archive: Archive,
    inputFiles: File[],
    indexedFiles: IndexedFiles,
  ): ArchiveEntry<Archive>[] {
    if (this.options.shouldExtract() || this.options.getAllowExcessSets()) {
      // We don't particularly care where input files come from
      return [];
    }

    /**
     * Find the entry paths of the Archive's entries that match a ROM from this Game. We identify
     * used entries by path rather than by checksum because a Game can have multiple ROMs that share
     * a checksum but have different names. Within an archive, entry paths are unique, so the path
     * identifies the entry unambiguously.
     */
    const usedEntryPaths = new Set(
      inputFiles
        .filter(
          (file): file is ArchiveEntry<Archive> =>
            file.getFilePath() === archive.getFilePath() &&
            file instanceof ArchiveEntry &&
            file.getArchive() === archive,
        )
        .map((entry) => entry.getExtractedFilePath()),
    );

    // Find which of the Archive's entries didn't match to a ROM from this Game
    return (indexedFiles.getFilesByFilePath().get(archive.getFilePath()) ?? []).filter(
      (file): file is ArchiveEntry<Archive> => {
        if (!(file instanceof ArchiveEntry)) {
          // A non-archive file exists at the same path as the archive (this shouldn't happen)
          return false;
        }
        if (file.getArchive() !== archive) {
          // This archive entry is coming from a different archive at the same path (probably a
          // different archive type)
          return false;
        }
        if (
          archive instanceof ChdBinCue &&
          file.getExtractedFilePath().toLowerCase().endsWith('.cue')
        ) {
          return false;
        }
        return !usedEntryPaths.has(file.getExtractedFilePath());
      },
    );
  }

  private async getOutputFile(
    dat: DAT,
    game: Game,
    rom: ROM,
    inputFile: File,
  ): Promise<File | undefined> {
    // Determine the output file's path
    let outputPathParsed: OutputPath;
    try {
      outputPathParsed = OutputFactory.getPath(this.options, dat, game, rom, inputFile);
    } catch (error) {
      this.prefixedLogger.trace(`${dat.getName()}: ${game.getName()}: ${error}`);
      return undefined;
    }
    const outputFilePath = outputPathParsed.format();

    // Determine the output CRC of the file
    let outputFileCrc32 = inputFile.getCrc32();
    let outputFileMd5 = inputFile.getMd5();
    let outputFileSha1 = inputFile.getSha1();
    let outputFileSha256 = inputFile.getSha256();
    let outputFileSize = inputFile.getSize();

    if (inputFile.getFileHeader()) {
      outputFileCrc32 = inputFile.getCrc32WithoutHeader();
      outputFileMd5 = inputFile.getMd5WithoutHeader();
      outputFileSha1 = inputFile.getSha1WithoutHeader();
      outputFileSha256 = inputFile.getSha256WithoutHeader();
      outputFileSize = inputFile.getSizeWithoutHeader();
    }

    if (inputFile.getPaddings().length === 1) {
      const desiredPadding = inputFile.getPaddings()[0];
      outputFileCrc32 = desiredPadding.getCrc32();
      outputFileMd5 = desiredPadding.getMd5();
      outputFileSha1 = desiredPadding.getSha1();
      outputFileSha256 = desiredPadding.getSha256();
      outputFileSize = desiredPadding.getPaddedSize();
    }

    // Determine the output file type
    if (
      (this.options.shouldZipRom(rom) && !(inputFile instanceof ArchiveFile)) ||
      (!this.options.shouldWrite() &&
        inputFile instanceof ArchiveEntry &&
        inputFile.getArchive() instanceof Zip)
    ) {
      // Should zip, return an archive entry within an output zip
      return await ArchiveEntry.entryOf({
        archive: new Zip(outputFilePath),
        entryPath: outputPathParsed.entryPath,
        size: outputFileSize,
        crc32: outputFileCrc32,
        md5: outputFileMd5,
        sha1: outputFileSha1,
        sha256: outputFileSha256,
      });
    }
    // Otherwise, return a raw file
    return await File.fileOf({
      filePath: outputFilePath,
      size: outputFileSize,
      crc32: outputFileCrc32,
      md5: outputFileMd5,
      sha1: outputFileSha1,
      sha256: outputFileSha256,
    });
  }

  private async generateWriteCandidates(
    dat: DAT,
    game: Game,
    foundRomsWithFiles: ROMWithFiles[],
  ): Promise<WriteCandidate[]> {
    // Explode the {@link Game} into one copy per region, language, and category found, so that
    // every {@link WriteCandidate} carries at most one of each. Output token replacement can then
    // simply use the first (and only) value.
    const explodedGames = (game.getRegions().length > 0 ? game.getRegions() : [undefined]).flatMap(
      (region) =>
        (game.getLanguages().length > 0 ? game.getLanguages() : [undefined]).flatMap((language) =>
          (game.getCategories().length > 0 ? game.getCategories() : [undefined]).map((category) =>
            game.withProps({
              region,
              language,
              categories: category === undefined ? [] : [category],
            }),
          ),
        ),
    );
    const writeCandidates = (
      await Promise.all(
        explodedGames.map(async (explodedGame) => {
          const romWithFiles = (
            await Promise.all(
              foundRomsWithFiles.map(async (romWithFiles) => {
                const outputFile = await this.getOutputFile(
                  dat,
                  explodedGame,
                  romWithFiles.getRom(),
                  romWithFiles.getInputFile(),
                );
                if (!outputFile) {
                  return undefined;
                }
                return new ROMWithFiles(
                  romWithFiles.getRom(),
                  romWithFiles.getInputFile(),
                  outputFile,
                );
              }),
            )
          ).filter((romWithFiles) => romWithFiles !== undefined);
          return new WriteCandidate(explodedGame, romWithFiles);
        }),
      )
    ).filter(ArrayUtil.filterUniqueMapped((candidate) => candidate.hashCode()));

    // Note: we're explicitly not de-duplicating input+output pairs here, even though they might
    //  all be the same for every ROM in the case of raw-copying/moving an archive; it is expected
    //  that CandidateWriter is smart enough to not duplicate the file writes
    return writeCandidates;
  }
}
